import { useState } from "react";
import { getErrorMessage } from "@/lib/errors";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { Plus, Trash2, TrendingUp, Trophy } from "lucide-react";
import { motion } from "framer-motion";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useGoals, useAccounts, useWallets, useTransactions } from "@/hooks/queries";
import { useAddGoal, useUpdateGoal, useDeleteGoal } from "@/hooks/mutations";
import { useAccountBalances } from "@/hooks/useAccountBalances";
import { parseLocalDate } from "@/lib/dates";

const goalSchema = z.object({
  name: z.string().trim().min(1).max(100),
  target_amount: z.number().positive().max(1000000000),
  target_date: z.string().optional(),
  description: z.string().max(500).optional(),
});

interface GoalsProps {
  embedded?: boolean;
}

const Goals = ({ embedded = false }: GoalsProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [targetAmount, setTargetAmount] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [description, setDescription] = useState("");
  const [motivation, setMotivation] = useState("");
  const [createPocket, setCreatePocket] = useState(false);
  const { toast } = useToast();
  const { parseNumber, decimalSeparator, getPlaceholder, formatCurrency, preferences } = useUserPreferences();

  // React Query hooks
  const { data: goals = [], isLoading } = useGoals();
  const { data: accounts = [] } = useAccounts();
  const { data: wallets = [] } = useWallets();
  const { data: transactions = [] } = useTransactions();
  const addGoal = useAddGoal();
  const updateGoal = useUpdateGoal();
  const deleteGoal = useDeleteGoal();

  // Calculate account balances from transactions
  const { getAccountBalance } = useAccountBalances(
    transactions,
    accounts.map(a => ({
      id: a.id,
      name: a.name,
      initial_balance: a.initial_balance,
      balance_as_of_date: a.balance_as_of_date,
    })),
    wallets.map(w => ({ id: w.id, account_id: w.account_id, name: w.name, balance: w.balance }))
  );

  const handleAddGoal = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const validatedData = goalSchema.parse({
        name,
        target_amount: parseNumber(targetAmount),
        target_date: targetDate || undefined,
        description: description.trim() || undefined,
      });

      let linkedAccountId: string | null = null;

      if (createPocket) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Not authenticated");

        const { data: newAccount, error: accErr } = await supabase
          .from("accounts")
          .insert({
            user_id: user.id,
            name: validatedData.name,
            account_type: "savings",
            initial_balance: 0,
            currency: preferences?.currency || "USD",
            exclude_from_dashboard: false,
          })
          .select()
          .single();

        if (accErr) throw accErr;
        linkedAccountId = newAccount.id;
      }

      await addGoal.mutateAsync({
        name: validatedData.name,
        title: validatedData.name,
        target_amount: validatedData.target_amount,
        target_date: validatedData.target_date || null,
        description: validatedData.description || null,
        motivation: motivation.trim() || null,
        linked_account_id: linkedAccountId,
      });

      toast({
        title: "Success",
        description: createPocket
          ? `Goal created with a linked savings pocket "${validatedData.name}"`
          : "Goal created successfully",
      });

      setDialogOpen(false);
      setName("");
      setTargetAmount("");
      setTargetDate("");
      setDescription("");
      setMotivation("");
      setCreatePocket(false);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: getErrorMessage(error),
          variant: "destructive",
        });
      }
    }
  };

  const handleUpdateProgress = async (goalId: string, newAmount: number) => {
    try {
      await updateGoal.mutateAsync({
        id: goalId,
        current_amount: newAmount,
      });

      toast({
        title: "Success",
        description: "Progress updated",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteGoal.mutateAsync(id);

      toast({
        title: "Success",
        description: "Goal deleted",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const getEffectiveAmount = (goal: (typeof goals)[number]): number => {
    if (goal.linked_account_id) {
      const linkedAccount = accounts.find(a => a.id === goal.linked_account_id);
      if (linkedAccount) {
        return Math.max(0, getAccountBalance(linkedAccount));
      }
    }
    return goal.current_amount || 0;
  };

  const getProgress = (goal: (typeof goals)[number]): number => {
    return Math.min((getEffectiveAmount(goal) / goal.target_amount) * 100, 100);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className={embedded ? "" : "container mx-auto px-4 py-8"}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="space-y-8"
      >
        {!embedded ? (
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Financial Goals</h1>
              <p className="text-muted-foreground">Set and track your savings goals</p>
            </div>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Goal
            </Button>
          </div>
        ) : (
          <div className="flex justify-end">
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Goal
            </Button>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          {goals.map((goal) => {
            const linkedAccount = goal.linked_account_id
              ? accounts.find(a => a.id === goal.linked_account_id)
              : null;
            const effectiveAmount = getEffectiveAmount(goal);
            const achieved = getProgress(goal) >= 100;

            return (
              <Card key={goal.id} className={achieved ? "border-yellow-500/50 bg-yellow-500/5" : ""}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {achieved ? (
                        <Trophy className="h-5 w-5 text-yellow-500" />
                      ) : (
                        <TrendingUp className="h-5 w-5 text-primary-text" />
                      )}
                      <CardTitle className="text-lg">{goal.name}</CardTitle>
                      {achieved && (
                        <span className="text-xs font-medium text-yellow-600 dark:text-yellow-400 bg-yellow-500/15 px-2 py-0.5 rounded-full">
                          Achieved 🏆
                        </span>
                      )}
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(goal.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {goal.description && (
                    <p className="text-sm text-muted-foreground">{goal.description}</p>
                  )}
                  {goal.motivation && (
                    <p className="text-xs italic text-primary-text/70">✦ {goal.motivation}</p>
                  )}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        {formatCurrency(effectiveAmount)} of {formatCurrency(goal.target_amount)}
                      </span>
                      <span className="font-medium">{getProgress(goal).toFixed(0)}%</span>
                    </div>
                    <Progress value={getProgress(goal)} />
                  </div>
                  {goal.target_date && (
                    <p className="text-xs text-muted-foreground">
                      Target: {parseLocalDate(goal.target_date).toLocaleDateString()}
                    </p>
                  )}
                  {linkedAccount ? (
                    <div className="space-y-1 text-xs text-muted-foreground">
                      <p className="font-medium text-foreground">{linkedAccount.name}</p>
                      <p>↑ <span className="font-medium">Transfer</span> from another account to add progress</p>
                      <p>↓ <span className="font-medium">Record a transaction</span> on this account when spending from it</p>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Amount (negative to correct)"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            const input = e.target as HTMLInputElement;
                            const newAmount = (goal.current_amount || 0) + parseFloat(input.value || "0");
                            handleUpdateProgress(goal.id, newAmount);
                            input.value = "";
                          }
                        }}
                      />
                      <Button
                        size="sm"
                        onClick={(e) => {
                          const input = (e.currentTarget.previousElementSibling as HTMLInputElement);
                          const newAmount = (goal.current_amount || 0) + parseFloat(input.value || "0");
                          handleUpdateProgress(goal.id, newAmount);
                          input.value = "";
                        }}
                      >
                        Add
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {goals.length === 0 && (
          <Card>
            <CardContent className="py-12 flex flex-col items-center gap-4">
              <p className="text-center text-muted-foreground">
                No goals yet. Create your first financial goal to start saving!
              </p>
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create your first goal
              </Button>
            </CardContent>
          </Card>
        )}
      </motion.div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Goal</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddGoal} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Goal Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Emergency Fund"
                  maxLength={100}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Target Amount</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={targetAmount}
                  onChange={(e) => setTargetAmount(e.target.value)}
                  placeholder={getPlaceholder()}
                  required
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground">Use "{decimalSeparator}" for decimals</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Target Date (Optional)</Label>
              <Input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={500}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>What's your why? (Optional)</Label>
              <Textarea
                value={motivation}
                onChange={(e) => setMotivation(e.target.value)}
                placeholder="e.g. For my family's security, Freedom to travel, Retire early…"
                maxLength={300}
                rows={2}
              />
              <p className="text-xs text-muted-foreground">Your motivation helps personalize your daily financial impulses.</p>
            </div>

            {/* Savings pocket toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border/50">
              <div>
                <Label className="text-sm font-medium">Create a savings pocket</Label>
                <p className="text-xs text-muted-foreground">
                  A dedicated account that tracks this goal's progress automatically
                </p>
              </div>
              <Switch checked={createPocket} onCheckedChange={setCreatePocket} />
            </div>

            <Button type="submit">
              <Plus className="h-4 w-4 mr-2" />
              Create Goal
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Goals;
